import { ReactNode, useState, createContext, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';

type NotificationContentOverrideContextType = {
  getNotificationContentOverride: (notificationId: string) => string | null
  setNotificationContentOverride: (notificationId: string, value: string | null) => void
};

const NotificationContentOverrideContext = createContext<NotificationContentOverrideContextType | undefined>(undefined)

export function useNotificationContentOverride() {
  const context = useContext(NotificationContentOverrideContext)
  if (!context) {
    throw new Error('useNotificationContentOverride must be used within a NotificationContentOverrideProvider')
  }
  const { getNotificationContentOverride, setNotificationContentOverride: setNotificationContentOverrideForNotification } = context
  const { id: notificationId } = useParams()

  const notificationContentOverride = notificationId ? getNotificationContentOverride(notificationId) : null

  const setNotificationContentOverride = useCallback((value: string | null) => {
    if (notificationId) {
      setNotificationContentOverrideForNotification(notificationId, value)
    }
  }, [notificationId, setNotificationContentOverrideForNotification])

  return { notificationContentOverride, setNotificationContentOverride }
}


type NotificationContentOverrideProviderProps = {
  children: ReactNode;
}

export function NotificationContentOverrideProvider({ children }: NotificationContentOverrideProviderProps) {
  const [overrides, setOverrides] = useState<Record<string, string>>({})

  const getNotificationContentOverride = useCallback((notificationId: string): string | null => {
    return overrides[notificationId] ?? null
  }, [overrides])

  const setNotificationContentOverride = useCallback((notificationId: string, value: string | null) => {
    setOverrides(prev => {
      if (value === null) {
        const { [notificationId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [notificationId]: value }
    })
  }, [])

  return (
    <NotificationContentOverrideContext.Provider value={{ getNotificationContentOverride, setNotificationContentOverride }}>
      {children}
    </NotificationContentOverrideContext.Provider>
  );
}
